import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  FileText, 
  MoreHorizontal, 
  Upload, 
  Eye, 
  Trash2, 
  CheckCircle,
  XCircle,
  Clock,
  History,
  Send,
  Loader2,
  Package,
  Link,
  Download,
  FileDown,
  ChevronDown,
  LayoutTemplate,
  Image,
  FileSpreadsheet,
  Presentation,
  File,
  Maximize2,
  X,
  Edit,
  Save,
  Tag,
  Calendar,
  User,
  FolderOpen,
  ArrowUp,
  ArrowDown,
  GripVertical,
  ArrowUpDown
} from 'lucide-react';
import { format } from 'date-fns';
import AISearch from '../components/AISearch';

// Default column configuration
const DEFAULT_COLUMNS = [
  { id: 'select', label: '', sortable: false, width: 'w-10' },
  { id: 'name', label: 'DOCUMENT', sortable: true, width: 'min-w-[200px]' },
  { id: 'document_number', label: 'DOC NUMBER', sortable: true, width: 'w-32' },
  { id: 'document_type', label: 'TYPE', sortable: true, width: 'w-24' },
  { id: 'revision_code', label: 'REV', sortable: true, width: 'w-20' },
  { id: 'status', label: 'STATUS', sortable: true, width: 'w-28' },
  { id: 'created_by_name', label: 'CREATOR', sortable: true, width: 'w-32' },
  { id: 'created_at', label: 'CREATED', sortable: true, width: 'w-28' },
  { id: 'actions', label: '', sortable: false, width: 'w-20' }
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Documents = () => {
  const { token, hasPermission } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [assets, setAssets] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [revisionCodes, setRevisionCodes] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [approvalCodes, setApprovalCodes] = useState([]);
  const [statusCodes, setStatusCodes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [versions, setVersions] = useState([]);
  const [previewInfo, setPreviewInfo] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [isEditingDetail, setIsEditingDetail] = useState(false);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  
  // Sorting and column ordering state
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [draggedColumn, setDraggedColumn] = useState(null);
  
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    project_id: '',
    asset_ids: [],
    document_type_id: '',
    document_number: '',
    revision_code_id: '',
    discipline_id: '',
    approval_code_id: '',
    status_code_id: '',
    tags: [],
    metadata: {}
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [metadataFields, setMetadataFields] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_id: '',
    asset_ids: [],
    document_type_id: '',
    document_number: '',
    revision_code_id: '',
    discipline_id: '',
    approval_code_id: '',
    status_code_id: '',
    tags: ''
  });
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    template_id: '',
    project_id: ''
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');

  const fetchDocuments = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await axios.get(`${API_URL}/api/documents`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setDocuments(response.data.documents);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data.projects);
    } catch (error) {
      console.error('Failed to load projects');
    }
  }, [token]);

  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data.assets);
    } catch (error) {
      console.error('Failed to load assets');
    }
  }, [token]);

  const fetchDocumentTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/document-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocumentTypes(response.data.document_types);
    } catch (error) {
      console.error('Failed to load document types');
    }
  }, [token]);

  const fetchRevisionCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/revision-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRevisionCodes(response.data.revision_codes);
    } catch (error) {
      console.error('Failed to load revision codes');
    }
  }, [token]);

  const fetchDisciplines = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/disciplines`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDisciplines(response.data.disciplines);
    } catch (error) {
      console.error('Failed to load disciplines');
    }
  }, [token]);

  const fetchApprovalCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/approval-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApprovalCodes(response.data.approval_codes);
    } catch (error) {
      console.error('Failed to load approval codes');
    }
  }, [token]);

  const fetchStatusCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/status-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatusCodes(response.data.status_codes);
    } catch (error) {
      console.error('Failed to load status codes');
    }
  }, [token]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/document-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates);
    } catch (error) {
      console.error('Failed to load templates');
    }
  }, [token]);

  const fetchMetadataFields = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/metadata-fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetadataFields(response.data.fields);
    } catch (error) {
      console.error('Failed to load metadata fields');
    }
  }, [token]);

  // Load user preferences for table settings
  const loadUserPreferences = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const prefs = response.data;
      
      if (prefs.documents_table) {
        // Apply sort preferences
        if (prefs.documents_table.sort_column) {
          setSortColumn(prefs.documents_table.sort_column);
        }
        if (prefs.documents_table.sort_direction) {
          setSortDirection(prefs.documents_table.sort_direction);
        }
        // Apply column order preferences
        if (prefs.documents_table.column_order && Array.isArray(prefs.documents_table.column_order)) {
          const orderedColumns = prefs.documents_table.column_order
            .map(id => DEFAULT_COLUMNS.find(c => c.id === id))
            .filter(Boolean);
          // Add any missing columns at the end
          DEFAULT_COLUMNS.forEach(col => {
            if (!orderedColumns.find(c => c.id === col.id)) {
              orderedColumns.push(col);
            }
          });
          setColumns(orderedColumns);
        }
      }
      setPreferencesLoaded(true);
    } catch (error) {
      console.error('Failed to load user preferences');
      setPreferencesLoaded(true);
    }
  }, [token]);

  // Save user preferences for table settings
  const saveTablePreferences = useCallback(async (newSortColumn, newSortDirection, newColumns) => {
    try {
      await axios.put(`${API_URL}/api/user/preferences/documents-table`, {
        sort_column: newSortColumn,
        sort_direction: newSortDirection,
        column_order: newColumns.map(c => c.id)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to save table preferences');
    }
  }, [token]);

  const fetchPreviewInfo = useCallback(async (docId) => {
    setPreviewLoading(true);
    setPreviewBlobUrl(null);
    try {
      const response = await axios.get(`${API_URL}/api/documents/${docId}/preview-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewInfo(response.data);
      
      // If it's an image or PDF, fetch the blob for preview
      if (response.data.has_file && (response.data.preview_type === 'image' || response.data.preview_type === 'pdf')) {
        try {
          const blobResponse = await axios.get(`${API_URL}/api/documents/${docId}/preview`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          });
          const blobUrl = window.URL.createObjectURL(blobResponse.data);
          setPreviewBlobUrl(blobUrl);
        } catch (blobError) {
          console.error('Failed to load preview blob');
        }
      }
    } catch (error) {
      console.error('Failed to load preview info');
      setPreviewInfo(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [token]);

  const fetchDocumentDetail = useCallback(async (docId) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDocument(response.data);
      setShowDetailModal(true);
      
      // Also fetch preview info
      fetchPreviewInfo(docId);
    } catch (error) {
      toast.error('Failed to load document details');
    }
  }, [token, fetchPreviewInfo]);

  // Get file icon based on file type
  const getFileIcon = (fileName) => {
    if (!fileName) return <File className="w-8 h-8" />;
    const ext = fileName.split('.').pop()?.toLowerCase();
    
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
      return <Image className="w-8 h-8 text-green-500" />;
    } else if (ext === 'pdf') {
      return <FileText className="w-8 h-8 text-red-500" />;
    } else if (['doc', 'docx', 'odt'].includes(ext)) {
      return <FileText className="w-8 h-8 text-blue-500" />;
    } else if (['xls', 'xlsx', 'ods'].includes(ext)) {
      return <FileSpreadsheet className="w-8 h-8 text-green-600" />;
    } else if (['ppt', 'pptx', 'odp'].includes(ext)) {
      return <Presentation className="w-8 h-8 text-orange-500" />;
    }
    return <File className="w-8 h-8 text-gray-500" />;
  };

  // Get Office Online viewer URL
  const getOfficeViewerUrl = (docId) => {
    const fileUrl = `${API_URL}/api/documents/${docId}/preview`;
    // Microsoft Office Online Viewer
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
  };

  // Open preview modal
  const openPreview = (doc) => {
    setSelectedDocument(doc);
    fetchPreviewInfo(doc.id);
    setShowPreviewModal(true);
  };

  useEffect(() => {
    fetchDocuments();
    fetchProjects();
    fetchAssets();
    fetchDocumentTypes();
    fetchRevisionCodes();
    fetchDisciplines();
    fetchApprovalCodes();
    fetchStatusCodes();
    fetchTemplates();
    fetchMetadataFields();
    loadUserPreferences();
  }, [fetchDocuments, fetchProjects, fetchAssets, fetchDocumentTypes, fetchRevisionCodes, fetchDisciplines, fetchApprovalCodes, fetchStatusCodes, fetchTemplates, fetchMetadataFields, loadUserPreferences]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/documents`, {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        project_id: formData.project_id || null,
        asset_ids: formData.asset_ids,
        document_type_id: formData.document_type_id || null,
        document_number: formData.document_number || null,
        revision_code_id: formData.revision_code_id || null,
        discipline_id: formData.discipline_id || null,
        approval_code_id: formData.approval_code_id || null,
        status_code_id: formData.status_code_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document created successfully');
      setShowCreateModal(false);
      setFormData({ name: '', description: '', project_id: '', asset_ids: [], document_type_id: '', document_number: '', revision_code_id: '', discipline_id: '', approval_code_id: '', status_code_id: '', tags: '' });
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create document');
    }
  };

  const toggleAssetSelection = (assetId) => {
    setFormData(prev => ({
      ...prev,
      asset_ids: prev.asset_ids.includes(assetId)
        ? prev.asset_ids.filter(id => id !== assetId)
        : [...prev.asset_ids, assetId]
    }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !selectedDocument) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      await axios.post(`${API_URL}/api/documents/${selectedDocument.id}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('File uploaded successfully');
      setShowUploadModal(false);
      setUploadFile(null);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await axios.delete(`${API_URL}/api/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleSubmitForApproval = async (docId) => {
    try {
      await axios.post(`${API_URL}/api/documents/${docId}/submit-approval`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document submitted for approval');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for approval');
    }
  };

  const handleApprovalAction = async (action) => {
    if (!selectedDocument) return;

    try {
      await axios.post(`${API_URL}/api/documents/${selectedDocument.id}/approval-action`, {
        action,
        comment: approvalComment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Document ${action}ed successfully`);
      setShowApprovalModal(false);
      setApprovalComment('');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  const fetchVersions = async (docId) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/${docId}/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVersions(response.data.versions);
      setShowVersionsModal(true);
    } catch (error) {
      toast.error('Failed to load versions');
    }
  };

  // Toggle document selection for bulk operations
  const toggleDocumentSelection = (docId) => {
    setSelectedDocuments(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  // Select/deselect all documents
  const toggleSelectAll = () => {
    if (selectedDocuments.length === filteredDocuments.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(filteredDocuments.map(d => d.id));
    }
  };

  // Bulk status update
  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedDocuments.length === 0) {
      toast.error('No documents selected');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/documents/bulk/status`, {
        document_ids: selectedDocuments,
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Updated ${response.data.modified_count} documents`);
      setSelectedDocuments([]);
      setShowBulkModal(false);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update documents');
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedDocuments.length === 0) {
      toast.error('No documents selected');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedDocuments.length} documents?`)) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/documents/bulk/delete`, {
        document_ids: selectedDocuments
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Deleted ${response.data.deleted_count} documents`);
      setSelectedDocuments([]);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete documents');
    }
  };

  // Start editing document detail
  const startEditingDetail = () => {
    if (!selectedDocument) return;
    setEditFormData({
      name: selectedDocument.name || '',
      description: selectedDocument.description || '',
      project_id: selectedDocument.project_id || '',
      asset_ids: selectedDocument.asset_ids || [],
      document_type_id: selectedDocument.document_type_id || '',
      document_number: selectedDocument.document_number || '',
      revision_code_id: selectedDocument.revision_code_id || '',
      discipline_id: selectedDocument.discipline_id || '',
      approval_code_id: selectedDocument.approval_code_id || '',
      status_code_id: selectedDocument.status_code_id || '',
      tags: selectedDocument.tags || [],
      metadata: selectedDocument.metadata || {}
    });
    setIsEditingDetail(true);
  };

  // Cancel editing
  const cancelEditingDetail = () => {
    setIsEditingDetail(false);
    setEditFormData({
      name: '',
      description: '',
      project_id: '',
      asset_ids: [],
      document_type_id: '',
      document_number: '',
      revision_code_id: '',
      discipline_id: '',
      approval_code_id: '',
      status_code_id: '',
      tags: [],
      metadata: {}
    });
  };

  // Save document edit
  const handleSaveEdit = async () => {
    if (!selectedDocument) return;
    setSavingEdit(true);
    try {
      await axios.put(`${API_URL}/api/documents/${selectedDocument.id}`, {
        name: editFormData.name,
        description: editFormData.description,
        project_id: editFormData.project_id || null,
        asset_ids: editFormData.asset_ids,
        document_type_id: editFormData.document_type_id || null,
        document_number: editFormData.document_number || null,
        revision_code_id: editFormData.revision_code_id || null,
        discipline_id: editFormData.discipline_id || null,
        approval_code_id: editFormData.approval_code_id || null,
        status_code_id: editFormData.status_code_id || null,
        tags: editFormData.tags,
        metadata: editFormData.metadata
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document updated successfully');
      setIsEditingDetail(false);
      fetchDocuments();
      
      // Refresh the selected document
      const response = await axios.get(`${API_URL}/api/documents/${selectedDocument.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDocument(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document');
    } finally {
      setSavingEdit(false);
    }
  };

  // Toggle asset in edit form
  const toggleEditAssetSelection = (assetId) => {
    setEditFormData(prev => ({
      ...prev,
      asset_ids: prev.asset_ids.includes(assetId)
        ? prev.asset_ids.filter(id => id !== assetId)
        : [...prev.asset_ids, assetId]
    }));
  };

  // Handle tag input in edit form
  const handleEditTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const value = e.target.value.trim();
      if (value && !editFormData.tags.includes(value)) {
        setEditFormData(prev => ({
          ...prev,
          tags: [...prev.tags, value]
        }));
        e.target.value = '';
      }
    }
  };

  const removeEditTag = (tagToRemove) => {
    setEditFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle metadata field change in edit form
  const handleEditMetadataChange = (fieldName, value) => {
    setEditFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [fieldName]: value
      }
    }));
  };

  // Download document file
  const handleDownload = async (doc) => {
    if (!doc.file_path) {
      toast.error('No file attached to this document');
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name || `${doc.document_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download file');
    }
  };

  // Export single document metadata
  const handleExportDocument = async (doc, format = 'json') => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/${doc.id}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { format },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${doc.document_number}_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export document');
    }
  };

  // Export multiple documents
  const handleBulkExport = async (format = 'csv') => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/export/bulk`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { format, status: statusFilter !== 'all' ? statusFilter : undefined },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `documents_export_${date}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export documents');
    }
  };

  // Create document from template
  const handleCreateFromTemplate = async (e) => {
    e.preventDefault();
    if (!templateFormData.template_id || !templateFormData.name) {
      toast.error('Please select a template and enter a name');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/documents/from-template/${templateFormData.template_id}`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          name: templateFormData.name,
          project_id: templateFormData.project_id || null
        }
      });
      toast.success('Document created from template');
      setShowTemplateModal(false);
      setTemplateFormData({ name: '', template_id: '', project_id: '' });
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create document from template');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { variant: 'secondary', label: 'Draft' },
      pending_approval: { variant: 'warning', label: 'Pending', className: 'bg-orange-500/10 text-orange-500' },
      approved: { variant: 'success', label: 'Approved', className: 'bg-green-500/10 text-green-500' },
      rejected: { variant: 'destructive', label: 'Rejected' },
      changes_requested: { variant: 'warning', label: 'Changes Needed', className: 'bg-yellow-500/10 text-yellow-500' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  // Handle column sorting
  const handleSort = (columnId) => {
    const column = columns.find(c => c.id === columnId);
    if (!column?.sortable) return;
    
    let newSortColumn = columnId;
    let newSortDirection;
    
    if (sortColumn === columnId) {
      newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newSortDirection);
    } else {
      newSortDirection = 'asc';
      setSortColumn(newSortColumn);
      setSortDirection(newSortDirection);
    }
    
    // Save preferences
    saveTablePreferences(newSortColumn, newSortDirection, columns);
  };

  // Handle column drag start
  const handleDragStart = (e, columnId) => {
    if (columnId === 'select' || columnId === 'actions') return;
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle column drag over
  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    if (columnId === 'select' || columnId === 'actions' || !draggedColumn) return;
  };

  // Handle column drop
  const handleDrop = (e, targetColumnId) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetColumnId) {
      setDraggedColumn(null);
      return;
    }
    if (targetColumnId === 'select' || targetColumnId === 'actions') {
      setDraggedColumn(null);
      return;
    }

    const newColumns = [...columns];
    const draggedIndex = newColumns.findIndex(c => c.id === draggedColumn);
    const targetIndex = newColumns.findIndex(c => c.id === targetColumnId);
    
    if (draggedIndex !== -1 && targetIndex !== -1) {
      const [draggedItem] = newColumns.splice(draggedIndex, 1);
      newColumns.splice(targetIndex, 0, draggedItem);
      setColumns(newColumns);
      
      // Save preferences
      saveTablePreferences(sortColumn, sortDirection, newColumns);
    }
    setDraggedColumn(null);
  };

  // Get sort icon for column
  const getSortIcon = (columnId) => {
    if (sortColumn !== columnId) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-primary" />
      : <ArrowDown className="w-3 h-3 ml-1 text-primary" />;
  };

  // Sort documents
  const sortDocuments = (docs) => {
    return [...docs].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'document_number':
          aVal = a.document_number || '';
          bVal = b.document_number || '';
          break;
        case 'document_type':
          aVal = a.document_type?.code || '';
          bVal = b.document_type?.code || '';
          break;
        case 'revision_code':
          aVal = a.revision_code?.code || '';
          bVal = b.revision_code?.code || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'created_by_name':
          aVal = a.created_by_name?.toLowerCase() || '';
          bVal = b.created_by_name?.toLowerCase() || '';
          break;
        case 'created_at':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredDocuments = documents.filter(doc =>
    doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedAndFilteredDocuments = sortDocuments(filteredDocuments);

  return (
    <div className="animate-fade-in">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Documents</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your documents and files
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasPermission('create_documents') && (
            <>
              <Button variant="outline" onClick={() => setShowTemplateModal(true)} data-testid="create-from-template-btn">
                <LayoutTemplate className="w-4 h-4 mr-2" />
                From Template
              </Button>
              <Button onClick={() => setShowCreateModal(true)} data-testid="create-document-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Document
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="p-8">
        {/* AI Search */}
        <div className="mb-6">
          <AISearch onSelectDocument={(doc) => fetchDocumentDetail(doc.id)} />
        </div>

        {/* Filters and Bulk Actions */}
        <div className="flex flex-wrap gap-4 mb-6 items-center justify-between">
          <div className="flex gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Filter documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-documents"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_approval">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex gap-2 items-center">
            {/* Bulk Actions Dropdown */}
            {selectedDocuments.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {selectedDocuments.length} selected
                </span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" data-testid="bulk-actions-btn">
                      Bulk Actions
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleBulkStatusUpdate('draft')}>
                      Set as Draft
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBulkStatusUpdate('pending_approval')}>
                      Submit for Approval
                    </DropdownMenuItem>
                    {hasPermission('approve_documents') && (
                      <>
                        <DropdownMenuItem onClick={() => handleBulkStatusUpdate('approved')}>
                          <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                          Approve All
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleBulkStatusUpdate('rejected')}>
                          <XCircle className="w-4 h-4 mr-2 text-red-500" />
                          Reject All
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    {hasPermission('manage_documents') && (
                      <DropdownMenuItem onClick={handleBulkDelete} className="text-destructive">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Selected
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
            
            {/* Export Button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-btn">
                  <FileDown className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleBulkExport('csv')}>
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkExport('json')}>
                  Export as JSON
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Documents Table */}
        <Card>
          <CardContent className="p-0">
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th 
                      key={column.id}
                      className={`${column.width || ''} ${column.sortable ? 'cursor-pointer select-none hover:bg-muted/50' : ''} ${draggedColumn === column.id ? 'opacity-50' : ''}`}
                      draggable={column.id !== 'select' && column.id !== 'actions'}
                      onDragStart={(e) => handleDragStart(e, column.id)}
                      onDragOver={(e) => handleDragOver(e, column.id)}
                      onDrop={(e) => handleDrop(e, column.id)}
                      onDragEnd={() => setDraggedColumn(null)}
                      onClick={() => column.sortable && handleSort(column.id)}
                      data-testid={`column-header-${column.id}`}
                    >
                      {column.id === 'select' ? (
                        <Checkbox
                          checked={selectedDocuments.length === sortedAndFilteredDocuments.length && sortedAndFilteredDocuments.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      ) : column.id === 'actions' ? (
                        <span className="text-right block">Actions</span>
                      ) : (
                        <div className="flex items-center gap-1">
                          {column.id !== 'select' && column.id !== 'actions' && (
                            <GripVertical className="w-3 h-3 opacity-30 cursor-grab" />
                          )}
                          <span className="uppercase text-xs tracking-wider">{column.label}</span>
                          {column.sortable && getSortIcon(column.id)}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : sortedAndFilteredDocuments.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-8 text-muted-foreground">
                      No documents found
                    </td>
                  </tr>
                ) : (
                  sortedAndFilteredDocuments.map((doc) => (
                    <tr key={doc.id} data-testid={`document-row-${doc.id}`} className={selectedDocuments.includes(doc.id) ? 'bg-muted/50' : ''}>
                      {columns.map((column) => (
                        <td key={column.id} className={column.id === 'actions' ? 'text-right' : ''}>
                          {column.id === 'select' && (
                            <Checkbox
                              checked={selectedDocuments.includes(doc.id)}
                              onCheckedChange={() => toggleDocumentSelection(doc.id)}
                              data-testid={`select-doc-${doc.id}`}
                            />
                          )}
                          {column.id === 'name' && (
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-muted">
                                <FileText className="w-4 h-4 text-muted-foreground" />
                              </div>
                              <div>
                                <button 
                                  className="font-medium text-left hover:text-primary hover:underline transition-colors cursor-pointer"
                                  onClick={() => fetchDocumentDetail(doc.id)}
                                  data-testid={`doc-name-${doc.id}`}
                                >
                                  {doc.name}
                                </button>
                                {doc.description && (
                                  <p className="text-xs text-muted-foreground truncate max-w-xs">
                                    {doc.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                          {column.id === 'document_number' && (
                            <span className="mono text-sm">
                              {doc.document_number ? (
                                <button 
                                  className="hover:text-primary hover:underline transition-colors cursor-pointer"
                                  onClick={() => fetchDocumentDetail(doc.id)}
                                  data-testid={`doc-number-${doc.id}`}
                                >
                                  {doc.document_number}
                                </button>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </span>
                          )}
                          {column.id === 'document_type' && (
                            <span className="text-sm">
                              {doc.document_type ? (
                                <Badge variant="outline">{doc.document_type.code}</Badge>
                              ) : '-'}
                            </span>
                          )}
                          {column.id === 'revision_code' && (
                            <span className="text-sm">
                              {doc.revision_code ? (
                                <Badge variant="secondary">{doc.revision_code.code}</Badge>
                              ) : '-'}
                            </span>
                          )}
                          {column.id === 'status' && getStatusBadge(doc.status)}
                          {column.id === 'created_by_name' && (
                            <span className="text-sm">{doc.created_by_name || 'Unknown'}</span>
                          )}
                          {column.id === 'created_at' && (
                            <span className="text-sm text-muted-foreground">
                              {format(new Date(doc.created_at), 'MMM d, yyyy')}
                            </span>
                          )}
                          {column.id === 'actions' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`doc-actions-${doc.id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => fetchDocumentDetail(doc.id)}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                {doc.file_path && (
                                  <DropdownMenuItem onClick={() => openPreview(doc)}>
                                    <Maximize2 className="w-4 h-4 mr-2" />
                                    Preview File
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => {
                                  setSelectedDocument(doc);
                                  setShowUploadModal(true);
                                }}>
                                  <Upload className="w-4 h-4 mr-2" />
                                  Upload File
                                </DropdownMenuItem>
                                {doc.file_path && (
                                  <DropdownMenuItem onClick={() => handleDownload(doc)}>
                                    <Download className="w-4 h-4 mr-2" />
                                    Download File
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => handleExportDocument(doc, 'json')}>
                                  <FileDown className="w-4 h-4 mr-2" />
                                  Export Metadata
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedDocument(doc);
                              fetchVersions(doc.id);
                            }}>
                              <History className="w-4 h-4 mr-2" />
                              View Versions
                            </DropdownMenuItem>
                            {doc.status === 'draft' && (
                              <DropdownMenuItem onClick={() => handleSubmitForApproval(doc.id)}>
                                <Send className="w-4 h-4 mr-2" />
                                Submit for Approval
                              </DropdownMenuItem>
                            )}
                            {doc.status === 'pending_approval' && hasPermission('approve_documents') && (
                              <DropdownMenuItem onClick={() => {
                                setSelectedDocument(doc);
                                setShowApprovalModal(true);
                              }}>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Review Approval
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {hasPermission('manage_documents') && (
                              <DropdownMenuItem 
                                onClick={() => handleDelete(doc.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Create Document Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Document Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="document-name-input"
              />
            </div>
            
            {/* Document Type and Number Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="document_type">Document Type</Label>
                <Select
                  value={formData.document_type_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, document_type_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="document-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select type</SelectItem>
                    {documentTypes.map((dt) => (
                      <SelectItem key={dt.id} value={dt.id}>
                        {dt.code} - {dt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="document_number">Document Number</Label>
                <Input
                  id="document_number"
                  value={formData.document_number}
                  onChange={(e) => setFormData({ ...formData, document_number: e.target.value })}
                  placeholder="Auto-generated if empty"
                  data-testid="document-number-input"
                />
              </div>
            </div>
            
            {/* Revision Code */}
            <div className="space-y-2">
              <Label htmlFor="revision_code">Revision Code</Label>
              <Select
                value={formData.revision_code_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, revision_code_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="revision-code-select">
                  <SelectValue placeholder="Select revision" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select revision</SelectItem>
                  {revisionCodes.map((rc) => (
                    <SelectItem key={rc.id} value={rc.id}>
                      {rc.code} - {rc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Discipline, Approval Code, Status Code */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="discipline">Discipline</Label>
                <Select
                  value={formData.discipline_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, discipline_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="discipline-select">
                    <SelectValue placeholder="Select discipline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select discipline</SelectItem>
                    {disciplines.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.code} - {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="approval_code">Approval Code</Label>
                <Select
                  value={formData.approval_code_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, approval_code_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="approval-code-select">
                    <SelectValue placeholder="Select approval" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select approval</SelectItem>
                    {approvalCodes.map((ac) => (
                      <SelectItem key={ac.id} value={ac.id}>
                        {ac.code} - {ac.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status_code">Status Code</Label>
                <Select
                  value={formData.status_code_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, status_code_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="status-code-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select status</SelectItem>
                    {statusCodes.map((sc) => (
                      <SelectItem key={sc.id} value={sc.id}>
                        {sc.code} - {sc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                data-testid="document-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="project">Project (Optional)</Label>
              <Select
                value={formData.project_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, project_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="document-project-select">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Project</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Link to Assets */}
            <div className="space-y-2">
              <Label>Link to Assets</Label>
              <div className="border rounded-md p-3 max-h-32 overflow-y-auto space-y-2">
                {assets.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No assets available</p>
                ) : (
                  assets.map((asset) => (
                    <div key={asset.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`asset-${asset.id}`}
                        checked={formData.asset_ids.includes(asset.id)}
                        onCheckedChange={() => toggleAssetSelection(asset.id)}
                      />
                      <Label htmlFor={`asset-${asset.id}`} className="text-sm cursor-pointer flex items-center gap-2">
                        <Package className="w-3 h-3 text-muted-foreground" />
                        {asset.name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
              {formData.asset_ids.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {formData.asset_ids.length} asset(s) selected
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="design, final, review"
                data-testid="document-tags-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="create-document-submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload File Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div 
              className="upload-dropzone"
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('dragover');
              }}
              onDragLeave={(e) => {
                e.currentTarget.classList.remove('dragover');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('dragover');
                if (e.dataTransfer.files?.[0]) {
                  setUploadFile(e.dataTransfer.files[0]);
                }
              }}
            >
              <input
                type="file"
                id="file-upload"
                className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="file-upload-input"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {uploadFile ? uploadFile.name : 'Drag & drop or click to select'}
                </p>
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!uploadFile || uploading} data-testid="upload-submit">
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Versions Modal */}
      <Dialog open={showVersionsModal} onOpenChange={setShowVersionsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {versions.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No versions available</p>
            ) : (
              versions.map((version, index) => (
                <div 
                  key={index} 
                  className={`p-3 rounded-lg border ${version.current ? 'border-primary bg-primary/5' : 'border-border'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium mono">v{version.version}</span>
                      {version.current && (
                        <Badge variant="outline" className="text-xs">Current</Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground mono">
                      {version.file_size ? `${(version.file_size / 1024).toFixed(1)} KB` : '-'}
                    </span>
                  </div>
                  {version.uploaded_at && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(new Date(version.uploaded_at), 'MMM d, yyyy HH:mm')}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Approval Modal */}
      <Dialog open={showApprovalModal} onOpenChange={setShowApprovalModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Document: {selectedDocument?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Comment (Optional)</Label>
              <Textarea
                value={approvalComment}
                onChange={(e) => setApprovalComment(e.target.value)}
                placeholder="Add a comment for the document owner..."
                rows={3}
                data-testid="approval-comment"
              />
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={() => handleApprovalAction('approve')}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="approve-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve
              </Button>
              <Button 
                onClick={() => handleApprovalAction('request_changes')}
                variant="outline"
                className="flex-1"
                data-testid="request-changes-btn"
              >
                <Clock className="w-4 h-4 mr-2" />
                Request Changes
              </Button>
              <Button 
                onClick={() => handleApprovalAction('reject')}
                variant="destructive"
                className="flex-1"
                data-testid="reject-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={(open) => {
        if (!open) {
          setIsEditingDetail(false);
          cancelEditingDetail();
        }
        setShowDetailModal(open);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedDocument && (
            <>
              <DialogHeader className="flex flex-row items-start justify-between">
                <div className="flex-1">
                  {isEditingDetail ? (
                    <Input
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      className="text-lg font-semibold"
                      placeholder="Document name"
                      data-testid="edit-doc-name"
                    />
                  ) : (
                    <DialogTitle className="flex items-center gap-3">
                      {getFileIcon(selectedDocument.file_name)}
                      {selectedDocument.name}
                    </DialogTitle>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {!isEditingDetail ? (
                    (hasPermission('edit_documents') || hasPermission('manage_documents')) && (
                      <Button variant="outline" size="sm" onClick={startEditingDetail} data-testid="edit-detail-btn">
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                    )
                  ) : (
                    <>
                      <Button variant="outline" size="sm" onClick={cancelEditingDetail}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleSaveEdit} disabled={savingEdit} data-testid="save-detail-btn">
                        {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        Save
                      </Button>
                    </>
                  )}
                </div>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Description */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Description</Label>
                  {isEditingDetail ? (
                    <Textarea
                      value={editFormData.description}
                      onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                      placeholder="Document description..."
                      rows={3}
                      data-testid="edit-doc-description"
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {selectedDocument.description || 'No description provided'}
                    </p>
                  )}
                </div>
                
                {/* File Preview Section */}
                {!isEditingDetail && (
                  <>
                    {previewLoading ? (
                      <div className="flex items-center justify-center h-48 bg-muted/30 rounded-lg">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : previewInfo?.has_file ? (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-muted/50 px-4 py-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {getFileIcon(previewInfo.file_name)}
                            <div>
                              <p className="text-sm font-medium">{previewInfo.file_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {previewInfo.file_size ? `${(previewInfo.file_size / 1024).toFixed(1)} KB` : 'Unknown size'}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => openPreview(selectedDocument)}>
                              <Maximize2 className="w-4 h-4 mr-1" />
                              Full Preview
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleDownload(selectedDocument)}>
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                        
                        {/* Inline Preview */}
                        <div className="bg-background">
                          {previewInfo.preview_type === 'image' && (
                            <div className="flex items-center justify-center p-4 max-h-64 overflow-hidden">
                              {previewBlobUrl ? (
                                <img
                                  src={previewBlobUrl}
                                  alt={previewInfo.file_name}
                                  className="max-w-full max-h-56 object-contain rounded"
                                />
                              ) : (
                                <div className="flex items-center justify-center h-32 text-muted-foreground">
                                  <Loader2 className="w-6 h-6 animate-spin" />
                                </div>
                              )}
                            </div>
                          )}
                          {previewInfo.preview_type === 'pdf' && (
                            <div className="h-64">
                              {previewBlobUrl ? (
                                <iframe
                                  src={previewBlobUrl}
                                  className="w-full h-full border-0"
                                  title="PDF Preview"
                                />
                              ) : (
                                <div className="flex items-center justify-center h-full text-muted-foreground">
                                  <Loader2 className="w-6 h-6 animate-spin" />
                                </div>
                              )}
                            </div>
                          )}
                          {previewInfo.preview_type === 'office' && (
                            <div className="p-4 text-center">
                              <div className="flex flex-col items-center gap-3 py-4">
                                {getFileIcon(previewInfo.file_name)}
                                <p className="text-sm text-muted-foreground">
                                  Office document - Click &quot;Full Preview&quot; to view in Microsoft Office Online
                                </p>
                              </div>
                            </div>
                          )}
                          {previewInfo.preview_type === 'other' && (
                            <div className="p-4 text-center">
                              <div className="flex flex-col items-center gap-3 py-4">
                                <File className="w-12 h-12 text-muted-foreground" />
                                <p className="text-sm text-muted-foreground">
                                  Preview not available for this file type
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="border border-dashed rounded-lg p-6 text-center">
                        <File className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">No file attached</p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-2"
                          onClick={() => {
                            setShowDetailModal(false);
                            setShowUploadModal(true);
                          }}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload File
                        </Button>
                      </div>
                    )}
                  </>
                )}
                
                {/* Document Metadata Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Document Number</Label>
                    {isEditingDetail ? (
                      <Input
                        value={editFormData.document_number}
                        onChange={(e) => setEditFormData({ ...editFormData, document_number: e.target.value })}
                        placeholder="DOC-001"
                        className="h-8"
                        data-testid="edit-doc-number"
                      />
                    ) : (
                      <p className="font-mono font-medium">{selectedDocument.document_number || '-'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Document Type</Label>
                    {isEditingDetail ? (
                      <Select
                        value={editFormData.document_type_id || "none"}
                        onValueChange={(value) => setEditFormData({ ...editFormData, document_type_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger className="h-8" data-testid="edit-doc-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {documentTypes.map((dt) => (
                            <SelectItem key={dt.id} value={dt.id}>
                              {dt.code} - {dt.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium">
                        {selectedDocument.document_type 
                          ? `${selectedDocument.document_type.code} - ${selectedDocument.document_type.name}`
                          : '-'}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Revision Code</Label>
                    {isEditingDetail ? (
                      <Select
                        value={editFormData.revision_code_id || "none"}
                        onValueChange={(value) => setEditFormData({ ...editFormData, revision_code_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger className="h-8" data-testid="edit-revision-code">
                          <SelectValue placeholder="Select revision" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {revisionCodes.map((rc) => (
                            <SelectItem key={rc.id} value={rc.id}>
                              {rc.code} - {rc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium">
                        {selectedDocument.revision_code 
                          ? `${selectedDocument.revision_code.code} - ${selectedDocument.revision_code.name}`
                          : '-'}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Discipline</Label>
                    {isEditingDetail ? (
                      <Select
                        value={editFormData.discipline_id || "none"}
                        onValueChange={(value) => setEditFormData({ ...editFormData, discipline_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger className="h-8" data-testid="edit-discipline">
                          <SelectValue placeholder="Select discipline" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {disciplines.map((d) => (
                            <SelectItem key={d.id} value={d.id}>
                              {d.code} - {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium">
                        {selectedDocument.discipline 
                          ? `${selectedDocument.discipline.code} - ${selectedDocument.discipline.name}`
                          : '-'}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Approval Code</Label>
                    {isEditingDetail ? (
                      <Select
                        value={editFormData.approval_code_id || "none"}
                        onValueChange={(value) => setEditFormData({ ...editFormData, approval_code_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger className="h-8" data-testid="edit-approval-code">
                          <SelectValue placeholder="Select approval" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {approvalCodes.map((ac) => (
                            <SelectItem key={ac.id} value={ac.id}>
                              {ac.code} - {ac.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium">
                        {selectedDocument.approval_code 
                          ? `${selectedDocument.approval_code.code} - ${selectedDocument.approval_code.name}`
                          : '-'}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Status Code</Label>
                    {isEditingDetail ? (
                      <Select
                        value={editFormData.status_code_id || "none"}
                        onValueChange={(value) => setEditFormData({ ...editFormData, status_code_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger className="h-8" data-testid="edit-status-code">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {statusCodes.map((sc) => (
                            <SelectItem key={sc.id} value={sc.id}>
                              {sc.code} - {sc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium">
                        {selectedDocument.status_code 
                          ? `${selectedDocument.status_code.code} - ${selectedDocument.status_code.name}`
                          : '-'}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Version</Label>
                    <p className="font-medium">v{selectedDocument.version}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">
                      <User className="w-3 h-3 inline mr-1" />
                      Creator
                    </Label>
                    <p className="font-medium">{selectedDocument.created_by_name || 'Unknown'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      Created
                    </Label>
                    <p className="font-medium">{format(new Date(selectedDocument.created_at), 'MMM d, yyyy HH:mm')}</p>
                  </div>
                </div>
                
                {/* Status */}
                <div className="flex items-center gap-4">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Status</Label>
                  {getStatusBadge(selectedDocument.status)}
                </div>
                
                {/* Project */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                    <FolderOpen className="w-3 h-3 inline mr-1" />
                    Project
                  </Label>
                  {isEditingDetail ? (
                    <Select
                      value={editFormData.project_id || "none"}
                      onValueChange={(value) => setEditFormData({ ...editFormData, project_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="edit-project">
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Project</SelectItem>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : selectedDocument.project_name ? (
                    <Badge variant="secondary">{selectedDocument.project_name}</Badge>
                  ) : (
                    <p className="text-sm text-muted-foreground">No project assigned</p>
                  )}
                </div>
                
                {/* Linked Assets */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                    <Link className="w-3 h-3 inline mr-1" />
                    Linked Assets
                  </Label>
                  {isEditingDetail ? (
                    <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
                      {assets.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No assets available</p>
                      ) : (
                        <div className="space-y-2">
                          {assets.map(asset => (
                            <div 
                              key={asset.id} 
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                editFormData.asset_ids.includes(asset.id) 
                                  ? 'bg-primary/10 border border-primary/30' 
                                  : 'bg-muted/30 hover:bg-muted/50'
                              }`}
                              onClick={() => toggleEditAssetSelection(asset.id)}
                            >
                              <Checkbox 
                                checked={editFormData.asset_ids.includes(asset.id)}
                                onChange={() => {}}
                              />
                              <Package className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm flex-1">{asset.name}</span>
                              <Badge variant="secondary" className="text-xs">{asset.asset_type}</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : selectedDocument.linked_assets?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedDocument.linked_assets.map(asset => (
                        <div key={asset.id} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                          <Package className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{asset.name}</span>
                          <Badge variant="secondary" className="text-xs">{asset.asset_type}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No linked assets</p>
                  )}
                </div>
                
                {/* Tags */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                    <Tag className="w-3 h-3 inline mr-1" />
                    Tags
                  </Label>
                  {isEditingDetail ? (
                    <div>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {editFormData.tags.map((tag, i) => (
                          <Badge key={i} variant="secondary" className="flex items-center gap-1">
                            {tag}
                            <X 
                              className="w-3 h-3 cursor-pointer hover:text-destructive" 
                              onClick={() => removeEditTag(tag)}
                            />
                          </Badge>
                        ))}
                      </div>
                      <Input
                        placeholder="Type and press Enter to add tags..."
                        onKeyDown={handleEditTagKeyDown}
                        className="h-8"
                        data-testid="edit-tags-input"
                      />
                    </div>
                  ) : selectedDocument.tags?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {selectedDocument.tags.map((tag, i) => (
                        <Badge key={i} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No tags</p>
                  )}
                </div>
                
                {/* Custom Metadata */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                    Custom Metadata
                  </Label>
                  {isEditingDetail ? (
                    <div className="space-y-3 p-3 border rounded-lg">
                      {metadataFields.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No custom metadata fields configured. Add them in Settings.</p>
                      ) : (
                        metadataFields.map(field => (
                          <div key={field.id} className="grid grid-cols-3 gap-2 items-center">
                            <Label className="text-sm">{field.name}</Label>
                            <div className="col-span-2">
                              {field.field_type === 'select' ? (
                                <Select
                                  value={editFormData.metadata[field.name] || ""}
                                  onValueChange={(value) => handleEditMetadataChange(field.name, value)}
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue placeholder={`Select ${field.name}`} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {field.options?.map((opt) => (
                                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : field.field_type === 'number' ? (
                                <Input
                                  type="number"
                                  value={editFormData.metadata[field.name] || ''}
                                  onChange={(e) => handleEditMetadataChange(field.name, e.target.value)}
                                  className="h-8"
                                />
                              ) : field.field_type === 'date' ? (
                                <Input
                                  type="date"
                                  value={editFormData.metadata[field.name] || ''}
                                  onChange={(e) => handleEditMetadataChange(field.name, e.target.value)}
                                  className="h-8"
                                />
                              ) : (
                                <Input
                                  value={editFormData.metadata[field.name] || ''}
                                  onChange={(e) => handleEditMetadataChange(field.name, e.target.value)}
                                  placeholder={`Enter ${field.name}`}
                                  className="h-8"
                                />
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  ) : selectedDocument.metadata && Object.keys(selectedDocument.metadata).length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 p-3 bg-muted/30 rounded-lg">
                      {Object.entries(selectedDocument.metadata).map(([key, value]) => (
                        <div key={key} className="text-sm">
                          <span className="text-muted-foreground">{key}:</span>{' '}
                          <span className="font-medium">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No custom metadata</p>
                  )}
                </div>
                
                {/* Comments (Read-only) */}
                {!isEditingDetail && selectedDocument.comments?.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                      Comments
                    </Label>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedDocument.comments.map(comment => (
                        <div key={comment.id} className="p-2 bg-muted/50 rounded text-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{comment.created_by_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(comment.created_at), 'MMM d, HH:mm')}
                            </span>
                          </div>
                          <p>{comment.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create from Template Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5" />
              Create from Template
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateFromTemplate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="template">Select Template</Label>
              <Select
                value={templateFormData.template_id || "none"}
                onValueChange={(value) => setTemplateFormData({ ...templateFormData, template_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="template-select">
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Choose a template</SelectItem>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                      {template.document_type && ` (${template.document_type.code})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {templates.length === 0 && (
                <p className="text-xs text-muted-foreground">No templates available. Create templates in Settings.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="template-name">Document Name</Label>
              <Input
                id="template-name"
                value={templateFormData.name}
                onChange={(e) => setTemplateFormData({ ...templateFormData, name: e.target.value })}
                placeholder="Enter document name"
                required
                data-testid="template-doc-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="template-project">Project (Optional)</Label>
              <Select
                value={templateFormData.project_id || "none"}
                onValueChange={(value) => setTemplateFormData({ ...templateFormData, project_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="template-project-select">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Project</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowTemplateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!templateFormData.template_id || !templateFormData.name} data-testid="create-from-template-submit">
                Create Document
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Full Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-5xl max-h-[95vh] p-0 overflow-hidden">
          {selectedDocument && previewInfo?.has_file && (
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
                <div className="flex items-center gap-3">
                  {getFileIcon(previewInfo.file_name)}
                  <div>
                    <p className="font-medium">{previewInfo.file_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {previewInfo.file_size ? `${(previewInfo.file_size / 1024).toFixed(1)} KB` : ''} • {selectedDocument.name}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleDownload(selectedDocument)}>
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setShowPreviewModal(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              
              <div className="flex-1 overflow-auto bg-background" style={{ height: 'calc(95vh - 60px)' }}>
                {previewInfo.preview_type === 'image' && (
                  <div className="flex items-center justify-center p-4 min-h-full">
                    {previewBlobUrl ? (
                      <img
                        src={previewBlobUrl}
                        alt={previewInfo.file_name}
                        className="max-w-full max-h-[80vh] object-contain"
                      />
                    ) : (
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                    )}
                  </div>
                )}
                
                {previewInfo.preview_type === 'pdf' && (
                  previewBlobUrl ? (
                    <iframe
                      src={previewBlobUrl}
                      className="w-full h-full border-0"
                      title="PDF Preview"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                    </div>
                  )
                )}
                
                {previewInfo.preview_type === 'office' && (
                  <div className="w-full h-full">
                    <iframe
                      src={getOfficeViewerUrl(selectedDocument.id)}
                      className="w-full h-full border-0"
                      title="Office Document Preview"
                    />
                  </div>
                )}
                
                {previewInfo.preview_type === 'other' && (
                  <div className="flex flex-col items-center justify-center h-full gap-4">
                    <File className="w-20 h-20 text-muted-foreground" />
                    <p className="text-lg text-muted-foreground">Preview not available for this file type</p>
                    <Button onClick={() => handleDownload(selectedDocument)}>
                      <Download className="w-4 h-4 mr-2" />
                      Download to View
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Documents;
